/**
 * Estado que não está no banco: o compromisso já começou e ainda não terminou.
 *
 * Vive aqui porque depende só do relógio de quem está olhando — gravar
 * "acontecendo" como status exigiria alguém escrevendo na hora certa, e o
 * registro ficaria errado no minuto seguinte.
 */

/** Status que ainda podem estar acontecendo. Cancelado e concluído, não. */
export const LIVE_STATUSES = ['scheduled', 'confirmed'];

/**
 * Rótulo e cor por status — lista, calendário, modal de detalhes e o select de
 * edição mostram o mesmo vocabulário. Uma cor por status só, não uma clara e
 * uma escura: o calendário usa `fg` sólido no bloco do evento, os outros usam
 * `fg` sobre `bg` claro no chip.
 */
export const APPOINTMENT_STATUS = {
  scheduled: { bg: '#dbeafe', fg: '#1d4ed8', label: 'Agendado' },
  confirmed: { bg: '#d1fae5', fg: '#047857', label: 'Confirmado' },
  completed: { bg: '#f1f5f9', fg: '#475569', label: 'Concluído' },
  cancelled: { bg: '#fee2e2', fg: '#b91c1c', label: 'Cancelado' },
  no_show: { bg: '#ffedd5', fg: '#c2410c', label: 'Não compareceu' },
};

/** Início e fim do compromisso, em ms — janela que "acontecendo agora" e "sem desfecho" checam. */
const getAppointmentWindow = (appointment) => {
  const start = new Date(appointment.startDatetime).getTime();
  const end = start + (appointment.durationMinutes || 60) * 60000;
  return { start, end };
};

export const isAppointmentHappeningNow = (appointment, now = new Date()) => {
  if (!appointment?.startDatetime) return false;
  if (!LIVE_STATUSES.includes(appointment.status)) return false;

  const { start, end } = getAppointmentWindow(appointment);
  if (Number.isNaN(start)) return false;

  const instant = now.getTime();
  return instant >= start && instant < end;
};

/**
 * Já terminou e ninguém disse o que aconteceu.
 *
 * Também é estado do relógio, não do banco: no minuto em que alguém marcar
 * Concluído ou Não compareceu, o alerta some sozinho — sem job varrendo tabela
 * para reescrever status.
 */
export const isAppointmentPendingOutcome = (appointment, now = new Date()) => {
  if (!appointment?.startDatetime) return false;
  if (!LIVE_STATUSES.includes(appointment.status)) return false;

  const { start, end } = getAppointmentWindow(appointment);
  if (Number.isNaN(start)) return false;

  return now.getTime() >= end;
};

/** Minutos que faltam para acabar — usado no rótulo "termina em X min". */
export const minutesUntilAppointmentEnds = (appointment, now = new Date()) => {
  const { end } = getAppointmentWindow(appointment);

  return Math.max(0, Math.ceil((end - now.getTime()) / 60000));
};
